import { useEffect, useState } from "react";
import { apiFetch } from "../config/api";
import { getUser, setSession } from "../config/auth";

function F({ label, children }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-3)", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {label}
      </label>
      {children}
    </div>
  );
}

export default function ProfilePage() {
  const localUser = getUser();

  const [profile, setProfile] = useState(null);
  const [departments, setDepartments] = useState([]);

  const [name, setName] = useState(localUser?.name || "");
  const [email, setEmail] = useState(localUser?.email || "");
  const [designation, setDesignation] = useState("");
  const [departmentId, setDepartmentId] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");

  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramLinkData, setTelegramLinkData] = useState(null);
  const [telegramError, setTelegramError] = useState("");

  useEffect(() => {
    loadProfile();
    loadDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadProfile() {
    try {
      const res = await apiFetch("/api/users/me");
      if (!res.ok) return;
      const data = await res.json();
      setProfile(data);
      setName(data.name || localUser?.name || "");
      setEmail(data.email || localUser?.email || "");
      // Guard: if designation was accidentally seeded with the email, treat as empty
      const safeDesignation = (data.designation && data.designation !== data.email) ? data.designation : "";
      setDesignation(safeDesignation);
      setDepartmentId(data.departmentId || "");
    } catch {}
  }

  async function loadDepartments() {
    try {
      const res = await apiFetch("/api/departments");
      if (!res.ok) return;
      const data = await res.json();
      setDepartments(Array.isArray(data) ? data : []);
    } catch {}
  }

  async function saveProfile() {
    if (saving) return;
    setSaveError("");
    setSaveSuccess("");

    if (newPassword && newPassword !== confirmPassword) {
      setSaveError("New passwords do not match.");
      return;
    }
    if (newPassword && !currentPassword) {
      setSaveError("Enter your current password to set a new one.");
      return;
    }

    setSaving(true);
    try {
      const body = {
        name: name.trim() || null,
        email: email.trim() || null,
        designation: designation.trim() || null,
        departmentId: departmentId || null,
        currentPassword: currentPassword || null,
        newPassword: newPassword || null,
      };

      const res = await apiFetch("/api/users/me", {
        method: "PUT",
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Failed to save profile");
      }

      const updated = await res.json();
      setProfile(updated);
      setName(updated.name || "");
      setEmail(updated.email || "");
      setDesignation(updated.designation || "");
      setDepartmentId(updated.departmentId || "");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSession({ user: { ...localUser, ...updated } });
      setSaveSuccess("Profile updated successfully.");
    } catch (err) {
      setSaveError(err.message || "Could not save profile.");
    } finally {
      setSaving(false);
    }
  }

  async function generateTelegramLink() {
    try {
      setTelegramLoading(true);
      setTelegramError("");
      const res = await apiFetch("/api/telegram/link-code");
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setTelegramLinkData(data);
    } catch {
      setTelegramError("Could not generate Telegram link code");
    } finally {
      setTelegramLoading(false);
    }
  }

  const displayRole = profile?.role ?? localUser?.role ?? "";
  const displayName = name || localUser?.name || "—";
  const initials = displayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

  const inp = (extra = {}) => ({
    width: "100%",
    padding: "9px 13px",
    borderRadius: 10,
    border: "1px solid var(--border-strong)",
    fontSize: 14,
    boxSizing: "border-box",
    background: "var(--surface)",
    color: "var(--text)",
    outline: "none",
    ...extra,
  });

  return (
    <div style={{ maxWidth: 1000 }}>

      {/* ── Profile banner ── */}
      <div className="glass-card" style={{ padding: "18px 28px", marginBottom: 20, display: "flex", alignItems: "center", gap: 18 }}>
        <div style={{
          width: 48, height: 48, borderRadius: "50%", flexShrink: 0,
          background: "linear-gradient(135deg, #0891b2, #6366f1)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 17, fontWeight: 700, color: "#fff",
        }}>
          {initials || "?"}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text)" }}>My Profile</div>
          <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>Update your personal details below</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          {displayRole && (
            <span style={{ padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600, background: "rgba(8,145,178,0.1)", color: "#0891b2" }}>
              {displayRole}
            </span>
          )}
          {designation && (
            <span style={{ padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600, background: "var(--surface-2)", color: "var(--text-2)", border: "1px solid var(--border-strong)" }}>
              {designation}
            </span>
          )}
        </div>
      </div>

      {/* ── Main two-column layout ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 20, alignItems: "start" }}>

        {/* ── Left: Edit Profile ── */}
        <div className="glass-card" style={{ padding: 28 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-3)", marginBottom: 20, textTransform: "uppercase", letterSpacing: "0.07em" }}>
            Account Information
          </div>

          <div style={{ display: "grid", gap: 16 }}>
            <F label="Full Name">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                autoComplete="name"
                style={inp()}
              />
            </F>

            <F label="Email Address">
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                autoComplete="email"
                style={inp()}
              />
            </F>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <F label="Designation">
                <input
                  value={designation}
                  onChange={(e) => setDesignation(e.target.value)}
                  placeholder="e.g. Senior Manager"
                  autoComplete="organization-title"
                  style={inp()}
                />
              </F>
              <F label="Role">
                <input
                  value={displayRole}
                  disabled
                  autoComplete="off"
                  style={inp({ background: "var(--surface-2)", color: "var(--text-3)", cursor: "not-allowed" })}
                />
              </F>
            </div>

            <F label="Department">
              <select
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
                autoComplete="off"
                style={inp()}
              >
                <option value="">— No department —</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </F>
          </div>

          {saveError && (
            <div style={{ marginTop: 16, padding: "10px 14px", borderRadius: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>
              {saveError}
            </div>
          )}
          {saveSuccess && (
            <div style={{ marginTop: 16, padding: "10px 14px", borderRadius: 8, background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.2)", color: "var(--success-text)", fontSize: 13 }}>
              {saveSuccess}
            </div>
          )}

          <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
            <button className="btn" onClick={saveProfile} disabled={saving} style={{ padding: "10px 26px", fontWeight: 600 }}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {/* ── Right column ── */}
        <div style={{ display: "grid", gap: 20 }}>

          {/* Change Password */}
          <div className="glass-card" style={{ padding: 28 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-3)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.07em" }}>
              Change Password
            </div>
            <p style={{ margin: "0 0 18px", fontSize: 13, color: "var(--text-3)", lineHeight: 1.5 }}>
              Leave blank to keep your current password unchanged.
            </p>

            <div style={{ display: "grid", gap: 14 }}>
              <F label="Current Password">
                <input
                  type="password"
                  placeholder="Enter current password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  style={inp()}
                />
              </F>
              <F label="New Password">
                <input
                  type="password"
                  placeholder="Min 6 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  style={inp()}
                />
              </F>
              <F label="Confirm New Password">
                <input
                  type="password"
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  style={inp({ borderColor: confirmPassword && confirmPassword !== newPassword ? "#f87171" : "var(--border-strong)" })}
                />
                {confirmPassword && confirmPassword !== newPassword && (
                  <div style={{ marginTop: 4, fontSize: 12, color: "var(--danger-text)" }}>Passwords do not match</div>
                )}
                {confirmPassword && confirmPassword === newPassword && newPassword && (
                  <div style={{ marginTop: 4, fontSize: 12, color: "var(--success)" }}>Passwords match ✓</div>
                )}
              </F>
            </div>
          </div>

          {/* Telegram */}
          <div className="glass-card" style={{ padding: 28 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(0,136,204,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                ✈️
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                Telegram Notifications
              </div>
            </div>

            <p style={{ margin: "0 0 16px", color: "var(--text-3)", fontSize: 13, lineHeight: 1.5 }}>
              Connect your Telegram account to receive instant task notifications.
            </p>

            <button className="btn" onClick={generateTelegramLink} disabled={telegramLoading} style={{ width: "100%", padding: "10px 0", fontWeight: 600 }}>
              {telegramLoading ? "Generating..." : "🔗 Connect Telegram"}
            </button>

            {telegramError && (
              <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>
                {telegramError}
              </div>
            )}

            {telegramLinkData && (
              <div style={{ marginTop: 16, padding: 16, background: "var(--surface-2)", borderRadius: 10, border: "1px solid var(--border-strong)", display: "grid", gap: 10 }}>
                <div style={{ fontSize: 13 }}>
                  <strong>Step 1:</strong>{" "}
                  <a href={`https://t.me/${telegramLinkData.botUsername}`} target="_blank" rel="noreferrer" style={{ color: "var(--brand)" }}>
                    t.me/{telegramLinkData.botUsername}
                  </a>
                </div>
                <div style={{ fontSize: 13 }}><strong>Step 2:</strong> Send this command:</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <code style={{ flex: 1, background: "var(--surface)", padding: "8px 12px", borderRadius: 8, border: "1px solid var(--border-strong)", fontSize: 13, fontFamily: "monospace" }}>
                    /link {telegramLinkData.code}
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(`/link ${telegramLinkData.code}`)}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid var(--border-strong)", background: "var(--surface)", cursor: "pointer", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    📋 Copy
                  </button>
                </div>
                <div style={{ fontSize: 12, color: "var(--text-3)" }}>Expires: {telegramLinkData.expiresAtUtc} UTC</div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
